import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Flag, Hourglass, Info, UserCog, UserRound, Waves } from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { tr } from '../../i18n/runtime';
import { PageHeader } from '../ui/PageHeader';
import { Card, Stack } from '../ui/Card';
import { DoneSplit } from '../ui/DoneSplit';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { Disclosure } from '../ui/Disclosure';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { MapWidget } from '../map/MapWidget';
import { DataGate } from './parts';
import { usePageTrail } from '../shell/ShellContext';
import { usePersona } from '../auth/RoleContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateShort,
  ha,
  haUnit,
  m3,
  num,
  pct,
  share,
  windowLabel,
  WORDS,
} from '../lib/format';
import type { WorkStatusValue } from '../map/paint';
import type {
  ContourMeta,
  Crop,
  Cycle,
  DateWindow,
  Farm,
  Obligation,
  WaterFile,
  Work,
  WorkStatus,
} from '../data/types';
import './work.css';
import { useAnalysis } from '../astra/useAnalysis';
import { scopeQuery, workHref, assignmentHref } from '../astra/model';
import { ContourPreview } from '../astra/ContourPreview';
import { Loading } from '../astra/shared';
import { ScopeControls } from '../astra/ScopeBar';
import { WorkPageSkeleton } from './work/WorkSkeleton';
import { SkeletonRegion, SkeletonTable, SkeletonText } from '../ui/Skeleton';
import { api } from '../../api/client';
import { seasonQuery } from '../data-source/api/season';
import { toWork } from '../data-source/api/works';
import { useRequest } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { WorkCampaign } from './work/WorkCampaign';
import { WateringDetail } from './WaterCanon';
import { irrigationWorks, toWaterWork } from '../data-source/api/water';
import { LoadError, failed } from '../ui/LoadError';
import { CampaignActions } from './agrocycle/CampaignActions';
import { toast } from '../ui/forms';

const m = defineMessages({
  closedAgo: {
    id: 'work.detail.window.closedAgo',
    defaultMessage: 'закрыто {days, plural, one {# день} few {# дня} many {# дней} other {# дня}} назад',
    description: 'Состояние окна работы на дату среза (шапка страницы работы): окно закрылось несколько дней назад; {days} — число дней',
  },
  closedToday: { id: 'work.detail.window.closedToday', defaultMessage: 'закрыто сегодня', description: 'Состояние окна работы на дату среза: окно закрылось сегодня' },
  activeLeft: {
    id: 'work.detail.window.activeLeft',
    defaultMessage: 'идёт, осталось {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}',
    description: 'Состояние окна работы на дату среза: окно идёт; {days} — сколько дней осталось',
  },
  activeToday: { id: 'work.detail.window.activeToday', defaultMessage: 'идёт, закрывается сегодня', description: 'Состояние окна работы на дату среза: окно закрывается сегодня' },
  activeLate: {
    id: 'work.detail.window.activeLate',
    defaultMessage: 'идёт, окно уже должно было закрыться {days, plural, one {# день} few {# дня} many {# дней} other {# дня}} назад',
    description: 'Состояние окна работы на дату среза: срок окна прошёл, но работа ещё помечена идущей; {days} — на сколько дней просрочено',
  },
  upcomingIn: {
    id: 'work.detail.window.upcomingIn',
    defaultMessage: 'впереди, откроется через {days, plural, one {# день} few {# дня} many {# дней} other {# дня}}',
    description: 'Состояние окна работы на дату среза: окно впереди; {days} — через сколько дней откроется',
  },
  upcomingToday: { id: 'work.detail.window.upcomingToday', defaultMessage: 'впереди, открывается сегодня', description: 'Состояние окна работы на дату среза: окно открывается сегодня' },
  upcoming: { id: 'work.detail.window.upcoming', defaultMessage: 'впереди', description: 'Состояние окна работы на дату среза: окно впереди' },
  waterAria: {
    id: 'work.detail.water.aria',
    defaultMessage: 'Подано {delivered} из {requested} запрошенных, покрытие {pct}',
    description: 'aria-label полосы «запрошено → подано»; {delivered}/{requested} — объёмы воды с единицей, {pct} — покрытие в процентах',
  },
  waterRequested: { id: 'work.detail.water.requested', defaultMessage: 'Запрошено', description: 'Факт под полосой воды: сколько воды запросили; рядом объём' },
  waterDelivered: { id: 'work.detail.water.delivered', defaultMessage: 'Подано', description: 'Факт под полосой воды: сколько воды подали; рядом объём' },
  waterCoverage: { id: 'work.detail.water.coverage', defaultMessage: 'Покрытие', description: 'Факт под полосой воды: доля поданной воды от запрошенной; рядом процент' },
  waterJournal: { id: 'work.detail.water.journal', defaultMessage: 'Журнал воды выбранной территории →', description: 'Ссылка под полосой воды: открыть журнал воды выбранной территории' },
  waterNote: {
    id: 'work.detail.water.note',
    defaultMessage: 'Хозяйств с полным объёмом: {full} · с частичным: {partial} · без подачи: {none}',
    description: 'Подпись под полосой воды: сколько хозяйств получили полный, частичный объём и ни одного; числа строкой',
  },
  farmsSkeleton: { id: 'work.detail.farms.skeleton', defaultMessage: 'хозяйства работы', description: 'Подпись для программ экрана (aria-label) пока загружается таблица хозяйств работы; винительный падеж' },
  farmsGenerating: { id: 'work.detail.farms.generating', defaultMessage: 'Данные ещё генерируются', description: 'Заголовок пустого состояния разбивки по хозяйствам, когда сервер данных не дал' },
  farmsNotReceived: { id: 'work.detail.farms.notReceived', defaultMessage: 'разбивка по хозяйствам не получена от сервера; повторите позже', description: 'Причина пустого состояния разбивки по хозяйствам' },
  levelFarm: { id: 'work.detail.trail.farm', defaultMessage: 'Хозяйство', description: 'Уровень в цепочке навигации над заголовком: хозяйство (для фермера)' },
  myFarm: { id: 'work.detail.myFarm', defaultMessage: 'Моё хозяйство', description: 'Название хозяйства фермера в цепочке навигации и в строке среза, когда имя не известно' },
  levelWork: { id: 'work.detail.trail.work', defaultMessage: 'Работа', description: 'Уровень в цепочке навигации над заголовком: работа' },
  levelDistrict: { id: 'work.detail.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком: район' },
  districtName: { id: 'work.detail.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; географическое имя — транслитерация' },
  levelCycle: { id: 'work.detail.trail.cycle', defaultMessage: 'Агроцикл', description: 'Уровень в цепочке навигации над заголовком: агроцикл' },
  levelStage: { id: 'work.detail.trail.stage', defaultMessage: 'Этап', description: 'Уровень в цепочке навигации над заголовком: этап агроцикла' },
  colFarm: { id: 'work.detail.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы: название хозяйства' },
  toScope: { id: 'work.detail.col.toScope', defaultMessage: 'В срез', description: 'Ссылка в строке хозяйства: сузить экран до этого хозяйства; коротко' },
  colContours: { id: 'work.detail.col.contours', defaultMessage: 'Контуры', description: 'Колонка таблицы хозяйств: число контуров' },
  colArea: { id: 'work.detail.col.area', defaultMessage: 'Площадь', description: 'Колонка таблицы: площадь; единица «га» в шапке рядом' },
  colReports: { id: 'work.detail.col.reports', defaultMessage: 'Отчёты', description: 'Колонка таблицы хозяйств: полоса долей отчётов' },
  denomFarmWorks: {
    id: 'work.detail.denom.farmWorks',
    defaultMessage: '{count, plural, one {работа на поле хозяйства в этой работе} few {работы на полях хозяйства в этой работе} many {работ на полях хозяйства в этой работе} other {работы на полях хозяйства в этой работе}}',
    description: 'Знаменатель полосы отчётов хозяйства («Знаменатель — 12 работ на полях хозяйства в этой работе»); без числа',
  },
  denomWorks: {
    id: 'work.detail.denom.works',
    defaultMessage: '{count, plural, one {работа на поле по этой работе плана} few {работы на полях по этой работе плана} many {работ на полях по этой работе плана} other {работы на полях по этой работе плана}}',
    description: 'Знаменатель полосы отчётов работы («Знаменатель — 312 работ на полях по этой работе плана»); без числа',
  },
  denomFieldWorks: {
    id: 'work.detail.denom.fieldWorks',
    defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}',
    description: 'Знаменатель пустой полосы отчётов; без числа',
  },
  denomFarms: {
    id: 'work.detail.denom.farms',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Слово-знаменатель под таблицей хозяйств («показано 5 из 24 хозяйств»); без числа',
  },
  denomReports: {
    id: 'work.detail.denom.reports',
    defaultMessage: '{count, plural, one {отчёт} few {отчёта} many {отчётов} other {отчёта}}',
    description: 'Слово-знаменатель под таблицей отчётов («показано 8 из 40 отчётов»); без числа',
  },
  windowNoteActive: { id: 'work.detail.windowNoteActive', defaultMessage: 'окно ещё идёт: пустой отчёт означает «ещё не срок».', description: 'Пояснение под полосой отчётов хозяйства, пока окно работы идёт' },
  colLate: { id: 'work.detail.col.late', defaultMessage: 'После срока без отчёта', description: 'Колонка таблицы хозяйств: сколько работ на полях хозяйства закрыты по сроку без отчёта' },
  notMeasured: { id: 'work.detail.notMeasured', defaultMessage: 'не измерено', description: 'Ячейка колонки «После срока без отчёта»: сервер не измерил' },
  no: { id: 'work.detail.no', defaultMessage: 'нет', description: 'Ячейка колонки «После срока без отчёта»: просроченных без отчёта нет' },
  colDate: { id: 'work.detail.col.date', defaultMessage: 'Дата', description: 'Колонка таблицы отчётов: дата отчёта' },
  colContour: { id: 'work.detail.col.contour', defaultMessage: 'Контур', description: 'Колонка таблицы отчётов: контур' },
  colVerdict: { id: 'work.detail.col.verdict', defaultMessage: 'Суждение', description: 'Колонка таблицы отчётов: проверен ли отчёт' },
  verified: { id: 'work.detail.verified', defaultMessage: 'проверено', description: 'Бейдж отчёта: проверен проверяющим' },
  reportedBadge: { id: 'work.detail.reportedBadge', defaultMessage: 'отчитались', description: 'Бейдж отчёта: отчёт внесён, проверки нет' },
  colAuthor: { id: 'work.detail.col.author', defaultMessage: 'Кто внёс', description: 'Колонка таблицы отчётов: автор отчёта' },
  enteredBy: { id: 'work.detail.enteredBy', defaultMessage: 'внёс {by}', description: 'Автор отчёта в строке таблицы и ленты; {by} — имя автора или тире' },
  onBehalf: { id: 'work.detail.onBehalf', defaultMessage: 'от имени {name}', description: 'В отчёте: менеджер внёс отчёт от имени хозяйства; {name} — имя хозяйства' },
  late: { id: 'work.detail.late', defaultMessage: 'с опозданием', description: 'Бейдж отчёта: отчёт внесён после окна' },
  contourNo: { id: 'work.detail.contourNo', defaultMessage: '№ {no}', description: 'Ссылка на контур по его номеру; {no} — номер контура' },
  farmNoApi: { id: 'work.detail.farmNo', defaultMessage: 'Хозяйство {id}', description: 'Название хозяйства, когда сервер имени не дал; {id} — служебный номер' },
  waterTitle: { id: 'work.detail.waterCard.title', defaultMessage: 'Вода полива', description: 'Заголовок карточки с водой полива на странице работы' },
  waterWhat: { id: 'work.detail.waterCard.what', defaultMessage: 'воду полива', description: 'Что не загрузилось (винительный падеж, подставляется в «Не удалось загрузить …»): вода полива' },
  waterSkeleton: { id: 'work.detail.waterCard.skeleton', defaultMessage: 'воду полива', description: 'Подпись для программ экрана (aria-label) пока загружается вода полива; винительный падеж' },
  irrigationNotFound: { id: 'work.detail.waterCard.notFound', defaultMessage: 'Полив не найден в картине сезона', description: 'Заголовок пустого состояния воды: эта работа не названа поливом' },
  irrigationNotFoundReason: { id: 'work.detail.waterCard.notFoundReason', defaultMessage: 'эта работа не названа поливом в сводке агроцикла на дату среза', description: 'Причина пустого состояния воды' },
  noWaterRequests: { id: 'work.detail.waterCard.noRequests', defaultMessage: 'Заявок на воду нет', description: 'Заголовок пустого состояния воды: заявок нет' },
  noWaterRequestsFallback: { id: 'work.detail.waterCard.noRequestsReason', defaultMessage: 'объём запрошенной воды по этому поливу не назван', description: 'Причина пустого состояния воды, когда сервер своей причины не дал' },
  reportsAt: { id: 'work.detail.waterCard.reportsAt', defaultMessage: 'в блоке «Ход работы» выше', description: 'Подсказка внутри картины воды: где смотреть отчёты по полям; «Ход работы» — заголовок карточки выше' },
  waterWhatFile: { id: 'work.detail.waterCard.whatFile', defaultMessage: 'полоса воды ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): полоса воды; {file} — имя файла данных, не переводить' },
  noRequestsReason: { id: 'work.detail.waterCard.noRequestsPeriod', defaultMessage: 'на эту работу воды не запрашивали в текущем срезе', description: 'Причина пустого состояния воды на фикстурах' },
  pageTitleFallback: { id: 'work.detail.title.fallback', defaultMessage: 'Работа {id}', description: 'Заголовок страницы, пока работа не прочитана; {id} — ключ работы из адреса' },
  purpose: { id: 'work.detail.purpose', defaultMessage: 'Контуры, отчёты и проверки этой работы на {date}. Выбранная территория сохраняется при переходе из сводки.', description: 'Подзаголовок страницы работы; {date} — дата среза' },
  purposeMissing: { id: 'work.detail.purposeMissing', defaultMessage: 'Работа в срезе не найдена — проверьте адрес или трек данных.', description: 'Подзаголовок страницы работы, когда работа по адресу не найдена' },
  stageTitle: { id: 'work.detail.stageTitle', defaultMessage: 'Этап', description: 'Подсказка у значка этапа в шапке страницы работы' },
  backToCycle: { id: 'work.detail.backToCycle', defaultMessage: 'Вернуться к агроциклу', description: 'Подсказка у ссылки с названием агроцикла в шапке страницы работы' },
  windowLine: { id: 'work.detail.windowLine', defaultMessage: '{window} · {state}', description: 'Строка окна в шапке страницы работы; {window} — окно дат, {state} — состояние окна («идёт, осталось 4 дня»)' },
  conditionTitle: { id: 'work.detail.conditionTitle', defaultMessage: 'Условность', description: 'Подсказка у значка условия работы в шапке страницы работы' },
  farmerScope: {
    id: 'work.detail.farmerScope',
    defaultMessage: '<b>{farm}</b> · показаны только работы на полях вашего хозяйства на {date}',
    description: 'Строка среза для фермера на странице работы; <b> — название хозяйства; {date} — дата среза',
  },
  farmCard: { id: 'work.detail.farmCard', defaultMessage: 'Карточка хозяйства', description: 'Ссылка в строке среза: открыть карточку хозяйства' },
  conflicts: { id: 'work.detail.conflicts', defaultMessage: 'Оспорено проверяющим: {count}. Отчёты сохранены; они не считаются подтверждёнными.', description: 'Предупреждение над страницей работы; {count} — сколько отчётов оспорил проверяющий' },
  reportsTitle: { id: 'work.detail.reports.cardTitle', defaultMessage: 'Отчёты по работам на полях', description: 'Заголовок карточки с долями отчётов на странице работы (фикстуры)' },
  whatState: { id: 'work.detail.what.state', defaultMessage: 'состояние работы ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): состояние работы; {file} — имя файла данных, не переводить' },
  windowNoteActiveLong: { id: 'work.detail.progress.windowActive', defaultMessage: 'окно ещё идёт: пустой отчёт означает «ещё не срок», а не невыполнение.', description: 'Пояснение под полосой отчётов работы, пока окно идёт' },
  windowNoteUpcoming: { id: 'work.detail.progress.windowUpcoming', defaultMessage: 'работа ещё не началась: отчёту рано появляться.', description: 'Пояснение под полосой отчётов работы, пока окно впереди' },
  lateReports: { id: 'work.detail.late.label', defaultMessage: 'Отчёты с опозданием', description: 'Подпись строки: сколько отчётов внесено после окна' },
  lateNote: { id: 'work.detail.late.note', defaultMessage: 'Доля от уже отчитавшихся, без заливки цветом: опоздание — факт даты отчёта, не статус выполнения.', description: 'Пояснение под строкой «Отчёты с опозданием»' },
  noWorkReason: { id: 'work.detail.progress.noWork', defaultMessage: 'работа с таким идентификатором в срезе отсутствует', description: 'Причина пустой полосы отчётов: работы по адресу нет' },
  nextTitle: { id: 'work.detail.next.title', defaultMessage: 'Следующий шаг по этой работе', description: 'Заголовок блока предложения следующего шага на странице работы (менеджер)' },
  nextBody: { id: 'work.detail.next.body', defaultMessage: 'Сверьте отчёт и источник проверки. Поручение сохранит выбранную работу, хозяйство, территорию, дату и исходные показатели.', description: 'Пояснение блока следующего шага на странице работы' },
  nextButton: { id: 'work.detail.next.button', defaultMessage: 'Поручить уточнение →', description: 'Кнопка блока следующего шага: создать поручение на уточнение работы' },
  assignTitle: { id: 'work.detail.assign.title', defaultMessage: 'Уточнить: {name}', description: 'Тема поручения, созданного со страницы работы; {name} — название работы' },
  assignBody: {
    id: 'work.detail.assign.body',
    defaultMessage:
      'На {date}: {reported, plural, one {# отчёт} few {# отчёта} many {# отчётов} other {# отчёта}}, {verified, plural, one {# проверка} few {# проверки} many {# проверок} other {# проверки}} из {total, plural, one {# работы на поле} other {# работ на полях}} выбранной работы и территории. Результат: указать источник, проверенные контуры, дату и оставшиеся вопросы. Данные демонстрационные.',
    description: 'Текст поручения, созданного со страницы работы; {date} — дата среза, остальное — числа',
  },
  farmsCardFarmer: { id: 'work.detail.farmsCard.titleFarmer', defaultMessage: 'Ваши работы на полях по этой работе', description: 'Заголовок карточки разбивки для фермера' },
  farmsCardTitle: { id: 'work.detail.farmsCard.title', defaultMessage: 'Разбивка по хозяйствам', description: 'Заголовок карточки разбивки по хозяйствам' },
  farmFind: { id: 'work.detail.farmsCard.find', defaultMessage: 'Найти хозяйство в работе', description: 'aria-label поля поиска хозяйства в таблице разбивки' },
  farmFindPlaceholder: { id: 'work.detail.farmsCard.findPlaceholder', defaultMessage: 'Найти хозяйство', description: 'Подсказка в пустом поле поиска хозяйства' },
  onlyMissing: { id: 'work.detail.farmsCard.onlyMissing', defaultMessage: 'Только без отчёта', description: 'Кнопка-переключатель над таблицей хозяйств: показать только хозяйства без полного отчёта' },
  whatFarms: { id: 'work.detail.what.farms', defaultMessage: 'разбивка по хозяйствам ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): разбивка по хозяйствам; {file} — имя файла данных, не переводить' },
  farmsEmptyTitle: { id: 'work.detail.farmsCard.emptyTitle', defaultMessage: 'Хозяйств нет', description: 'Заголовок пустой таблицы хозяйств' },
  farmsEmptyFilter: { id: 'work.detail.farmsCard.emptyFilter', defaultMessage: 'измените поиск или фильтр отчётов', description: 'Причина пустой таблицы хозяйств, когда включён поиск или фильтр' },
  farmsEmptyNone: { id: 'work.detail.farmsCard.emptyNone', defaultMessage: 'на эту работу не назначено ни одного хозяйства в текущем срезе', description: 'Причина пустой таблицы хозяйств' },
  mapTitle: { id: 'work.detail.map.title', defaultMessage: 'Карта работы', description: 'Заголовок карточки с картой работы (фикстуры)' },
  mapPreparingTitle: { id: 'work.detail.map.preparingTitle', defaultMessage: 'Статус по контурам готовится', description: 'Заголовок пустого состояния карты работы' },
  mapPreparingReason: { id: 'work.detail.map.preparingReason', defaultMessage: 'сервер пока не отдаёт состояние работ на полях этой работы по контурам района; по хозяйству оно видно в его карточке', description: 'Причина пустого состояния карты работы' },
  mapCaption: { id: 'work.detail.map.caption', defaultMessage: 'Контуры работы: проверено, отчитались, окно идёт, без отчёта', description: 'Подпись под картой работы (фикстуры)' },
  mapUnavailableTitle: { id: 'work.detail.map.unavailableTitle', defaultMessage: 'Карта недоступна', description: 'Заголовок пустого состояния: карты работы нет' },
  mapUnavailableReason: { id: 'work.detail.map.unavailableReason', defaultMessage: 'работа с таким идентификатором в срезе не найдена', description: 'Причина пустого состояния: работа по адресу не найдена' },
  reportsCard: { id: 'work.detail.reportsCard.title', defaultMessage: 'Отчёты', description: 'Заголовок карточки с лентой отчётов по работе' },
  reportsEmptyTitle: { id: 'work.detail.reportsCard.emptyTitle', defaultMessage: 'Отчётов пока нет', description: 'Заголовок пустого состояния ленты отчётов' },
  reportsEmptyReason: { id: 'work.detail.reportsCard.emptyReason', defaultMessage: 'по этой работе ещё не поступило ни одного отчёта в текущем срезе', description: 'Причина пустого состояния ленты отчётов' },
  reportsAll: { id: 'work.detail.reportsCard.all', defaultMessage: 'Все отчёты', description: 'Название раскрывающегося блока со всеми отчётами' },
  reportsTableEmptyTitle: { id: 'work.detail.reportsCard.tableEmptyTitle', defaultMessage: 'Отчётов нет', description: 'Заголовок пустой таблицы отчётов' },
  reportsTableEmptyReason: { id: 'work.detail.reportsCard.tableEmptyReason', defaultMessage: 'по этой работе ещё не поступило ни одного отчёта', description: 'Причина пустой таблицы отчётов' },
  reportCount: {
    id: 'work.detail.reportCount',
    defaultMessage: '{count, plural, one {# отчёт} few {# отчёта} many {# отчётов} other {# отчёта}}',
    description: 'Число отчётов в сводке раскрывающегося блока; {count} — число',
  },
});

interface FarmAgg {
  farmId: string;
  /** Имя из канона; у фикстур его нет — берётся из списка хозяйств. */
  name?: string;
  /** Канон: окно закрылось и отчёта нет. `null` — сервер не назвал. */
  overdue?: boolean | null;
  contours: number;
  areaHa: number;
  total: number;
  reported: number;
  verified: number;
}

/** «После срока без отчёта» по хозяйству: у закрывшегося окна это остаток без отчёта; неизвестное — не ноль. */
function lateOf(f: FarmAgg): number {
  return f.overdue ? Math.max(0, f.total - f.reported) : 0;
}

/** Целые дни между двумя ISO-датами: `to` минус `from`. */
function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** «Идёт / закрыто N дней назад / впереди» — состояние окна на дату среза. */
function windowStateLabel(status: WorkStatus, window: DateWindow, asOf: string): string {
  if (status === 'done') {
    const d = daysBetween(window.to, asOf);
    return d > 0 ? tr(m.closedAgo, { days: d }) : tr(m.closedToday);
  }
  if (status === 'active') {
    const d = daysBetween(asOf, window.to);
    if (d > 0) return tr(m.activeLeft, { days: d });
    if (d === 0) return tr(m.activeToday);
    return tr(m.activeLate, { days: -d });
  }
  const d = daysBetween(asOf, window.from);
  if (d > 0) return tr(m.upcomingIn, { days: d });
  if (d === 0) return tr(m.upcomingToday);
  return tr(m.upcoming);
}

/** Полоса «запрошено → подано»: две величины и покрытие. Вода — цвет воды, не оценка. */
function WaterStrip({ requested, delivered, coverage, note, link }: { requested: number | null; delivered: number | null; coverage: number | null; note: string; link: string }) {
  const t = useT();
  return (
    <div className="waterstrip">
      <div className="waterstrip__bar" role="img" aria-label={t(m.waterAria, { delivered: m3(delivered), requested: m3(requested), pct: pct(coverage, 1) })}>
        <div className="waterstrip__fill" style={{ width: `${Math.min(100, coverage ?? 0)}%` }} />
      </div>
      <div className="waterstrip__facts">
        <span><Icon icon={Waves} size={16} /> {t(m.waterRequested)} <span className="num">{m3(requested)}</span></span>
        <span>{t(m.waterDelivered)} <span className="num">{m3(delivered)}</span></span>
        <span>{t(m.waterCoverage)} <span className="num">{pct(coverage, 1)}</span></span>
      </div>
      <div className="t-caption">{note}</div>
      <Link to={link}>{t(m.waterJournal)}</Link>
    </div>
  );
}

/** Канон — ответ сервера со своим состоянием; фикстуры — файл трека. Текста «канон не отдаёт» у канона нет. */
function FarmGate({ onApi, req, res, what, children }: { onApi: boolean; req: { state: string; error: unknown }; res: Parameters<typeof DataGate<Obligation[]>>[0]['res']; what: string; children: () => ReactNode }) {
  const t = useT();
  if (!onApi) return <DataGate res={res} what={what}>{children}</DataGate>;
  if (req.state === 'loading') return <SkeletonTable label={t(m.farmsSkeleton)} rows={6} cols={5} />;
  if (req.error) return <EmptyState compact title={t(m.farmsGenerating)} reason={t(m.farmsNotReceived)} />;
  return <>{children()}</>;
}

/**
 * Страница работы. Решения менеджера по кампании (шапка) после выполнения
 * перечитывают страницу целиком: срок, охват и ход — разные запросы, и показывать
 * их вразнобой нельзя; поэтому тело пересоздаётся, а уведомление живёт снаружи.
 */
export function WorkDetail() {
  const [version, setVersion] = useState(0);
  return (
    <WorkDetailBody
      key={version}
      onDecided={(message) => {
        setVersion((n) => n + 1);
        toast.show({ message });
      }}
    />
  );
}

function WorkDetailBody({ onDecided }: { onDecided: (message: string) => void }) {
  const t = useT();
  const { id = '', workId = '' } = useParams();
  /* Фермеру этот экран открыт только про его собственное хозяйство: срез ему
     задаёт useAnalysis, а район — фильтры, поручение и водный баланс —
     не показывается вовсе (ROLES-SPEC, screen-levels: его уровень
     заканчивается хозяйством). */
  const { persona } = usePersona();
  const isFarmer = persona?.role === 'farmer';
  const [farmSearch,setFarmSearch] = useState(''), [onlyMissing,setOnlyMissing] = useState(false), [preview,setPreview] = useState<ContourMeta|null>(null);
  const analysis = useAnalysis({ cycleId: id, workId });
  const onApi = analysis.sourceKind === 'api';
  const { params: ctxParams, linkSearch } = useAnalysisContext();
  const [search] = useSearchParams();
  const location = useLocation();
  const [channel, setChannel] = useState<string | null>(null);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const works = useTrackData<Work[]>(id ? FILES.works(id) : null);
  const obligations = useTrackData<Obligation[]>(id ? FILES.obligations(id) : null);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);

  const cycle = (cycles.data ?? []).find((c) => c.id === id) ?? null;
  const planWork = (works.data ?? []).find((w) => w.id === workId) ?? null;
  /* Работа не из плана — кампания-приёмник «Карантинные предписания» или
     полива вне плана: в разрезе работ цикла её нет (ход цикла — только план,
     S7), поэтому строка читается картиной по самой кампании, без оси цикла:
     названная кампания видом не режется (Г1, журнал №948). */
  const needsOwnRow = onApi && works.state === 'ready' && !planWork && Number.isInteger(Number(workId));
  const ownRow = useRequest(
    () => (needsOwnRow ? api.seasonPicture(seasonQuery({ ...analysis.scope, cycleId: '' }, { campaign_id: Number(workId), summary: true, weeks: 0 })) : Promise.resolve(null)),
    [needsOwnRow, workId, analysis.scope],
  );
  const sourceWork = useMemo(() => {
    const row = ownRow.data?.summary?.works.find((w) => String(w.campaign_id) === workId);
    return row && ownRow.data ? toWork(row, id, 1, ownRow.data.horizon) : null;
  }, [ownRow.data, workId, id]);
  const originalWork = planWork ?? sourceWork;
  const projected = analysis.summary?.works.find(r => r.work.id === workId);
  const asOf = analysis.scope.asOf;
  const work = originalWork && projected ? { ...originalWork, status: (originalWork.window.from > asOf ? 'upcoming' : originalWork.window.to < asOf ? 'done' : 'active') as WorkStatus, totals: { ...originalWork.totals, obligations: projected.total, reported: projected.reported, verified: projected.verified, area_ha: projected.area, late: analysis.summary!.rows.filter(o => o.late).length } } : originalWork;

  const water = useTrackData<WaterFile>(id && work?.kind === 'irrigation' && !onApi ? FILES.water(id) : null);
  /* На каноне разбивка по хозяйствам и вода работы приходят с сервера: файлов
     фикстур там нет, и «Канон этого не отдаёт» было бы неправдой. */
  const campaign = useRequest(
    () => (onApi && Number.isInteger(Number(workId)) ? api.campaignProgress(Number(workId), ctxParams) : Promise.resolve(null)),
    [onApi, workId, ctxParams],
  );
  /* Работа в адресе — кампания; вода читается по определению работы, поэтому
     его берём из картины сезона (строка кампании несёт и то и другое). */
  const isIrrigation = onApi && work?.kind === 'irrigation';
  const pictureReq = useRequest(
    () => (isIrrigation
      ? sourceWork
        ? Promise.resolve(ownRow.data) // полив вне плана — строка уже прочитана по самой кампании
        : api.seasonPicture(seasonQuery(analysis.scope, { summary: true, weeks: 0 }))
      : Promise.resolve(null)),
    [isIrrigation, analysis.scope, sourceWork, ownRow.data],
  );
  const workDefId = pictureReq.data?.summary?.works.find(w => String(w.campaign_id) === workId)?.work_def_id ?? null;
  /* Полив — тем же запросом и тем же разбором, что и картина полива на
     экране «Вода» (`WateringDetail`): хозяйства, покрытие, каналы, карта
     подачи (agroai-6al.35). Кампания в запросе — этот полив, а не все
     кампании той же работы шаблона. */
  const waterReq = useRequest(
    () => (isIrrigation && workDefId !== null ? api.waterWork(workDefId, { ...ctxParams, campaign_id: Number(workId) }) : Promise.resolve(null)),
    [isIrrigation, workDefId, ctxParams, workId],
  );
  const irrigationWork = useMemo(
    () => (pictureReq.data ? irrigationWorks(pictureReq.data).find(w => String(w.campaignId) === workId) ?? null : null),
    [pictureReq.data, workId],
  );

  const rows = useMemo(
    () => (analysis.summary?.rows ?? []).map((o): Obligation => ({ ...o, state: o.verified ? 'reported_verified' : o.reported ? 'reported' : o.overdue ? 'silent' : o.work.window.from > asOf ? 'upcoming' : 'in_window' })),
    [analysis.summary, asOf],
  );

  const farmById = useMemo(() => {
    const map = new Map<string, Farm>();
    for (const f of farms.data ?? []) map.set(f.id, f);
    return map;
  }, [farms.data]);

  const contourById = useMemo(() => {
    const map = new Map<string, ContourMeta>();
    for (const c of contours.data ?? []) map.set(c.id, c);
    return map;
  }, [contours.data]);

  const apiNames = useMemo(() => new Map((campaign.data?.campaign.farms ?? []).map(f => [String(f.actor.id), f.actor.name ?? tr(m.farmNoApi, { id: String(f.actor.id) })])), [campaign.data]);
  const farmLabel = (farmId: string): string => farmById.get(farmId)?.name ?? apiNames.get(farmId) ?? farmId;
  const contourLabel = (contourId: string): string => {
    const c = contourById.get(contourId);
    return c ? t(m.contourNo, { no: String(c.no) }) : contourId;
  };

  const statusById = useMemo(() => {
    const map: Record<string, WorkStatusValue> = {};
    for (const row of rows) map[row.contour_id] = row.state;
    return map;
  }, [rows]);

  const contourIds = useMemo(() => rows.map((r) => r.contour_id), [rows]);

  const farmRows = useMemo(() => {
    if (onApi) {
      // `?farm=` (переход из кабинета хозяйства, agroai-6al.34): разбивка
      // сужается до этого хозяйства, как шапка и «отчитались» выше.
      const farmScope = analysis.scope.farmId;
      return (campaign.data?.campaign.farms ?? []).filter((f) => !farmScope || String(f.actor.id) === farmScope).map((f): FarmAgg => ({
        farmId: String(f.actor.id), name: f.actor.name ?? undefined, overdue: f.overdue,
        contours: f.contours.total, areaHa: (f.area.total_microha ?? 0) / 1_000_000,
        total: f.obligations.total, reported: f.obligations.reported, verified: f.obligations.verified,
      })).sort((a, b) => lateOf(b) - lateOf(a) || b.total - b.reported - (a.total - a.reported));
    }
    const map = new Map<string, FarmAgg>();
    for (const o of rows) {
      let agg = map.get(o.farm_id);
      if (!agg) {
        agg = { farmId: o.farm_id, contours: 0, areaHa: 0, total: 0, reported: 0, verified: 0 };
        map.set(o.farm_id, agg);
      }
      agg.contours += 1;
      agg.areaHa += o.area_ha;
      agg.total += 1;
      if (o.state === 'reported' || o.state === 'reported_verified') agg.reported += 1;
      if (o.state === 'reported_verified') agg.verified += 1;
    }
    // «Без отчёта» вниз: сначала хозяйства с наибольшим остатком без отчёта.
    return [...map.values()].sort((a, b) => b.total - b.reported - (a.total - a.reported));
  }, [rows, onApi, campaign.data, analysis.scope.farmId]);

  const reportRows = useMemo(
    () =>
      rows
        .filter((r): r is Obligation & { reported_on: string } => r.reported_on !== null)
        .sort((a, b) => b.reported_on.localeCompare(a.reported_on)),
    [rows],
  );

  const workWaterRows = useMemo(
    () => (analysis.summary?.waterRows ?? []).map(r => ({ requested_m3: r.requested, delivered_m3: r.delivered, status: r.delivered >= r.requested ? 'delivered' : r.delivered > 0 ? 'partial' : 'pending' })),
    [analysis.summary],
  );
  const apiBalance = waterReq.data?.balance ?? null;
  const waterTotals = useMemo(() => {
    if (onApi) {
      const requested = apiBalance?.requested_volume_m3 == null ? null : Number(apiBalance.requested_volume_m3);
      const delivered = apiBalance?.delivered_volume_m3 == null ? null : Number(apiBalance.delivered_volume_m3);
      // Доля — поле ответа сервера (С4), не частное двух чисел в браузере.
      return { requested, delivered, coverage: apiBalance?.coverage_pct ?? null };
    }
    const requested = workWaterRows.reduce((s, r) => s + r.requested_m3, 0);
    const delivered = workWaterRows.reduce((s, r) => s + r.delivered_m3, 0);
    return { requested: requested as number | null, delivered: delivered as number | null, coverage: share(delivered, requested) };
  }, [workWaterRows, onApi, apiBalance]);

  /** Цикл называется культурой и сезоном: «Хлопок 2026», а не голым «2026». */
  const cycleName = cycle ? cycleTitle(cycle, crops.data) : id;

  /* Цепочка без петель (agroai-6al.35): агроцикл ведёт в Агроциклы, этап — в
     Агроциклы с выбранным этапом, работа — последняя ступень. Прежде «Раздел»
     и «Цикл» вели на один и тот же адрес. Хозяйство из `?farm=` — фильтр
     этой страницы, а не ступень цепочки: он снимается плашкой «Все
     хозяйства работы». */
  const stageQuery = work?.stage ? (() => { const q = new URLSearchParams(analysis.query); q.set('stage', work.stage); q.delete('farm'); return `?${q}`; })() : analysis.query;
  usePageTrail(isFarmer
    ? [
        { level: t(m.levelFarm), name: persona?.farmName ?? persona?.farmId ?? t(m.myFarm), to: '/my/today' },
        { level: t(m.levelWork), name: work?.name ?? workId },
      ]
    : [
        // i18n-ignore: запасное имя района — собственное название, а не текст интерфейса
        { level: t(m.levelDistrict), name: t(m.districtName), to: `/${linkSearch()}` },
        { level: t(m.levelCycle), name: cycleName, to: `/cycles${analysis.query}` },
        ...(work?.stage ? [{ level: t(m.levelStage), name: work.stage, to: `/cycles${stageQuery}` }] : []),
        { level: t(m.levelWork), name: work?.name ?? workId },
      ]);

  /** Адрес этой же работы с фильтром хозяйства или без него — срез сохраняется. */
  const workHrefFor = (farm: string | null) => {
    const q = new URLSearchParams(search);
    if (farm) q.set('farm', farm);
    else q.delete('farm');
    const text = q.toString();
    return `${location.pathname}${text ? `?${text}` : ''}`;
  };
  const statusParams = { ...ctxParams, ...(analysis.scope.massifId && Number.isInteger(Number(analysis.scope.massifId)) ? { massif_id: Number(analysis.scope.massifId) } : {}) };

  /* С переходом едет только контекст анализа: цикл и дата среза. */
  const analysisContext = scopeQuery(analysis.scope, { massifId: undefined, farmId: undefined, workId: undefined });

  const farmColumns: Column<FarmAgg>[] = [
    {
      key: 'farm',
      header: t(m.colFarm),
      /* Имя хозяйства открывает его уровень; сужение этого экрана — отдельное
         действие «В срез» в той же строке (screen-levels.md, жест «вниз»). */
      render: (row) =>
        isFarmer ? (
          <span>{farmLabel(row.farmId)}</span>
        ) : (
          <>
            <Link to={`/farms/${row.farmId}${analysisContext}`}>{farmLabel(row.farmId)}</Link>{' '}
            <Link className="astra-link" to={workHref({ ...analysis.scope, farmId: row.farmId }, workId)}>
              {t(m.toScope)}
            </Link>
          </>
        ),
    },
    { key: 'contours', header: t(m.colContours), numeric: true, render: (row) => num(row.contours) },
    { key: 'area', header: t(m.colArea), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => ha(row.areaHa) },
    {
      key: 'split',
      header: t(m.colReports),
      width: '280px',
      /* Тот же DoneSplit, что и в карточке работы выше: развилка «сделано /
         не сделано» на одном экране читается одинаково, с третьей осью
         «без отчёта» и названным знаменателем. */
      render: (row) => (
        <DoneSplit
          size="sm"
          counts={{ total: row.total, reported: row.reported, verified: row.verified }}
          denominator={m.denomFarmWorks}
          windowNote={work?.status === 'active' ? t(m.windowNoteActive) : undefined}
        />
      ),
    },
  ];

  if (onApi) {
    farmColumns.push({
      key: 'late',
      header: t(m.colLate),
      numeric: true,
      render: (row) => row.overdue === null || row.overdue === undefined ? <span className="t-caption">{t(m.notMeasured)}</span> : row.overdue ? num(lateOf(row)) : <span className="t-caption">{t(m.no)}</span>,
    });
  }

  const allReportsColumns: Column<Obligation>[] = [
    { key: 'date', header: t(m.colDate), render: (row) => dateShort(row.reported_on) },
    {
      key: 'farm',
      header: t(m.colFarm),
      render: (row) =>
        isFarmer ? <span>{farmLabel(row.farm_id)}</span> : <Link to={`/farms/${row.farm_id}${analysisContext}`}>{farmLabel(row.farm_id)}</Link>,
    },
    {
      key: 'contour',
      header: t(m.colContour),
      render: (row) => <Link to={`/contours/${row.contour_id}${analysisContext}`}>{contourLabel(row.contour_id)}</Link>,
    },
    { key: 'area', header: t(m.colArea), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => ha(row.area_ha) },
    {
      key: 'verdict',
      header: t(m.colVerdict),
      render: (row) => (
        <Badge tone={row.state === 'reported_verified' ? 'green' : 'sage'}>
          {row.state === 'reported_verified' ? t(m.verified) : t(m.reportedBadge)}
        </Badge>
      ),
    },
    {
      key: 'author',
      header: t(m.colAuthor),
      render: (row) => (
        <span className="reportcell">
          <span>{t(m.enteredBy, { by: row.reported_by ?? '—' })}</span>
          {row.on_behalf ? <span className="reportcell__onbehalf">{t(m.onBehalf, { name: row.on_behalf })}</span> : null}
          {row.late ? (
            <Badge tone="neutral" icon={Hourglass}>
              {t(m.late)}
            </Badge>
          ) : null}
        </span>
      ),
    },
  ];

  if (analysis.loading || analysis.error) return <Loading analysis={analysis} skeleton={<WorkPageSkeleton />} />;
  /* Вода полива: на каноне — сразу под ходом работы (agroai-6al.35), той же
     картиной, что на экране «Вода»; на фикстурах — прежняя полоса. */
  const waterSection = work?.kind === 'irrigation' && !isFarmer ? (
        <div data-guide="water"><Card size="lg" title={t(m.waterTitle)} aside={onApi ? undefined : countOf(workWaterRows.length, WORDS.farm)}>
          {onApi ? (
            failed(pictureReq) || failed(waterReq) ? <LoadError compact what={t(m.waterWhat)} error={pictureReq.error ?? waterReq.error} onRetry={() => { pictureReq.reload(); waterReq.reload(); }} />
            : waterReq.state === 'loading' || pictureReq.state === 'loading' || (workDefId !== null && !waterReq.data) ? <SkeletonRegion label={t(m.waterSkeleton)} minHeight={220}><SkeletonText lines={5} /></SkeletonRegion>
            : !waterReq.data || !irrigationWork ? <EmptyState compact title={t(m.irrigationNotFound)} reason={t(m.irrigationNotFoundReason)} />
            : waterTotals.requested === null && !waterReq.data.allocations.length ? <EmptyState compact title={t(m.noWaterRequests)} reason={waterReq.data.empty_note || t(m.noWaterRequestsFallback)} />
            : <WateringDetail item={{ work: irrigationWork, view: toWaterWork(waterReq.data), raw: waterReq.data }} day={asOf} channel={channel} setChannel={setChannel} linkSearch={linkSearch} cycle={id} reportsAt={t(m.reportsAt)} />
          ) : (
          <DataGate res={water} what={t(m.waterWhatFile, { file: FILES.water(id) })}>
            {() =>
              workWaterRows.length === 0 ? (
                <EmptyState
                  compact
                  title={t(m.noWaterRequests)}
                  reason={t(m.noRequestsReason)}
                />
              ) : (
                <WaterStrip requested={waterTotals.requested} delivered={waterTotals.delivered} coverage={waterTotals.coverage} note={t(m.waterNote, { full: num(workWaterRows.filter((r) => r.status === 'delivered').length), partial: num(workWaterRows.filter((r) => r.status === 'partial').length), none: num(workWaterRows.filter((r) => r.status === 'pending').length) })} link={`/analysis/water${analysis.query}`} />
              )
            }
          </DataGate>
          )}
        </Card></div>
      ) : null;
  return (
    <Stack>
      <PageHeader
        level="work"
        title={work?.name ?? t(m.pageTitleFallback, { id: workId })}
        purpose={
          work
            ? t(m.purpose, { date: dateShort(asOf) })
            : t(m.purposeMissing)
        }
        actions={!isFarmer && onApi && work && Number.isInteger(Number(workId)) ? <CampaignActions cycleKey={id} campaignId={Number(workId)} onDone={onDecided} /> : null}
      >
        {work ? (
          <div className="workmeta">
            <span className="workmeta__stage">
              <Icon icon={Flag} size={16} title={t(m.stageTitle)} />
              {work.stage}
            </span>
            <span className="workmeta__sep" aria-hidden="true">
              ·
            </span>
            <Link className="workmeta__cycle" to={`/cycles${analysis.query}`} title={t(m.backToCycle)}>
              <Icon icon={ArrowLeft} size={16} />
              {cycleName}
            </Link>
            <span className="workmeta__sep" aria-hidden="true">
              ·
            </span>
            <span className="workmeta__window">
              {t(m.windowLine, { window: windowLabel(work.window.from, work.window.to), state: windowStateLabel(work.status, work.window, asOf) })}
            </span>
          </div>
        ) : null}
        {work?.note ? (
          <div className="worknote">
            <Icon icon={Info} size={16} title={t(m.conditionTitle)} />
            <span>{work.note}</span>
          </div>
        ) : null}
      </PageHeader>

      <div className="astra workscope">{isFarmer ? <p>{t(m.farmerScope, { b: c => <strong>{c}</strong>, farm: analysis.scope.farmId ? farmLabel(analysis.scope.farmId) : persona?.farmName ?? t(m.myFarm), date: dateShort(asOf) })}</p> : <><ScopeControls a={analysis} cycles={false} />{analysis.scope.farmId && !onApi && <p><strong>{farmLabel(analysis.scope.farmId)}</strong> · <Link to={`/farms/${analysis.scope.farmId}`}>{t(m.farmCard)}</Link></p>}</>}{!!projected?.conflicts && <p role="status" className="worknote workscope__conflicts">{t(m.conflicts, { count: projected.conflicts })}</p>}</div>

      {onApi && work && Number.isInteger(Number(workId)) ? (
        <WorkCampaign
          campaignId={Number(workId)}
          params={statusParams}
          pictureParams={seasonQuery(sourceWork ? { ...analysis.scope, cycleId: '' } : analysis.scope)}
          farmId={analysis.scope.farmId ?? null}
          work={{ name: work.name, window: work.window, status: work.status }}
          asOf={asOf}
          campaignFarms={campaign.data?.campaign.farms ?? []}
          campaignArea={campaign.data?.campaign.area_work ?? null}
          linkSearch={linkSearch()}
          workHrefFor={workHrefFor}
          isFarmer={isFarmer}
          demo={campaign.data?.campaign.epistemic_status === 'synthetic_demo'}
          afterProgress={waterSection}
        />
      ) : null}

      {onApi ? null : <div data-guide="progress"><Card size="lg" title={t(m.reportsTitle)} aside={work ? haUnit(work.totals.area_ha) : undefined}>
        <DataGate res={works} what={t(m.whatState, { file: FILES.works(id) })}>
          {() =>
            work ? (
              <>
                <DoneSplit
                  counts={{
                    total: work.totals.obligations,
                    reported: work.totals.reported,
                    verified: work.totals.verified,
                  }}
                  denominator={m.denomWorks}
                  windowNote={
                    work.status === 'active'
                      ? t(m.windowNoteActiveLong)
                      : work.status === 'upcoming'
                        ? t(m.windowNoteUpcoming)
                        : undefined
                  }
                />
                <div className="worklate">
                  <div className="worklate__line">
                    <Icon icon={Hourglass} size={16} />
                    <span>{t(m.lateReports)}</span>
                    <span className="worklate__value">
                      {num(work.totals.late)}
                      {work.totals.late !== null && share(work.totals.late, work.totals.reported) !== null
                        ? ` · ${pct(share(work.totals.late, work.totals.reported) as number)}`
                        : ''}
                    </span>
                  </div>
                  <div className="worklate__note">
                    {t(m.lateNote)}
                  </div>
                </div>
              </>
            ) : (
              <DoneSplit
                counts={null}
                denominator={m.denomFieldWorks}
                reason={t(m.noWorkReason)}
              />
            )
          }
        </DataGate>
      </Card></div>}

      {!isFarmer && work && projected && projected.total > 0 && <div className="astra-farm-bridge" data-guide="next-step"><div><strong>{t(m.nextTitle)}</strong><p>{t(m.nextBody)}</p></div><Link className="astra-button astra-button-primary" to={assignmentHref(t(m.assignTitle, { name: work.name }), t(m.assignBody, { date: asOf, reported: projected.reported, verified: projected.verified, total: projected.total }), analysis.scope)}>{t(m.nextButton)}</Link></div>}

      {/* Водный баланс здесь считается по хозяйствам выбранной территории —
          это величина района, и фермеру она не принадлежит. */}
      {onApi ? null : waterSection}

      {onApi ? null : <div data-guide="farms"><Card size="lg" title={isFarmer ? t(m.farmsCardFarmer) : t(m.farmsCardTitle)} aside={countOf(farmRows.length, WORDS.farm)}>{isFarmer ? null : <div className="astra-work-farm-filter"><input aria-label={t(m.farmFind)} placeholder={t(m.farmFindPlaceholder)} value={farmSearch} onChange={e => setFarmSearch(e.target.value)} /><button className="astra-button" aria-pressed={onlyMissing} onClick={() => setOnlyMissing(!onlyMissing)}>{t(m.onlyMissing)}</button></div>}
        <FarmGate onApi={onApi} req={campaign} res={obligations} what={t(m.whatFarms, { file: FILES.obligations(id) })}>
          {() => (
            <DataTable
              columns={farmColumns}
              rows={farmRows.filter(r => farmLabel(r.farmId).toLocaleLowerCase().includes(farmSearch.toLocaleLowerCase()) && (!onlyMissing || r.reported < r.total))}
              getKey={(row) => row.farmId}
              denominator={m.denomFarms}
              empty={{
                title: t(m.farmsEmptyTitle),
                reason: farmSearch || onlyMissing ? t(m.farmsEmptyFilter) : t(m.farmsEmptyNone),
              }}
            />
          )}
        </FarmGate>
      </Card></div>}

      {onApi ? null : <div data-guide="map"><Card size="lg" title={t(m.mapTitle)}>
        {work && onApi && contourIds.length === 0 ? (
          <EmptyState compact title={t(m.mapPreparingTitle)} reason={t(m.mapPreparingReason)} />
        ) : work ? (
          <MapWidget
            colorBy="workStatus"
            mode="frame"
            height={420}
            legend="below"
            onSelect={c => { if (c) setPreview(c); }}
            card="none"
            filterIds={contourIds}
            focusIds={contourIds.length > 0 ? contourIds : undefined}
            workStatusById={statusById}
            caption={t(m.mapCaption)}
          />
        ) : (
          <EmptyState compact title={t(m.mapUnavailableTitle)} reason={t(m.mapUnavailableReason)} />
        )}
      </Card></div>}

      {onApi ? null : <Card size="lg" title={t(m.reportsCard)}>
        {reportRows.length === 0 ? (
          <EmptyState
            compact
            title={t(m.reportsEmptyTitle)}
            reason={t(m.reportsEmptyReason)}
          />
        ) : (
          <Stack tight>
            <ul className="reportfeed">
              {reportRows.slice(0, 8).map((row) => (
                <li className="reportfeed__item" key={row.id}>
                  <div className="reportfeed__top">
                    <span className="reportfeed__date">{dateShort(row.reported_on)}</span>
                    {row.late ? (
                      <Badge tone="neutral" icon={Hourglass}>
                        {t(m.late)}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="reportfeed__where">
                    {isFarmer ? (
                      <span>{farmLabel(row.farm_id)}</span>
                    ) : (
                      <Link to={`/farms/${row.farm_id}${analysisContext}`}>{farmLabel(row.farm_id)}</Link>
                    )}
                    <span aria-hidden="true">·</span>
                    <Link to={`/contours/${row.contour_id}${analysisContext}`}>{contourLabel(row.contour_id)}</Link>
                  </div>
                  <div className="reportfeed__author">
                    <span className="reportfeed__actor">
                      <Icon icon={UserRound} size={16} /> {t(m.enteredBy, { by: row.reported_by ?? '—' })}
                    </span>
                    {row.on_behalf ? (
                      <span className="reportfeed__onbehalf">
                        <Icon icon={UserCog} size={16} /> {t(m.onBehalf, { name: row.on_behalf })}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>

            <Disclosure label={t(m.reportsAll)} summary={t(m.reportCount, { count: reportRows.length })}>
              <DataTable
                columns={allReportsColumns}
                rows={reportRows}
                getKey={(row) => row.id}
                denominator={m.denomReports}
                empty={{ title: t(m.reportsTableEmptyTitle), reason: t(m.reportsTableEmptyReason) }}
              />
            </Disclosure>
          </Stack>
        )}
      </Card>}

      {preview && <ContourPreview contour={preview} farms={analysis.farms} massifs={analysis.massifs} analysis={analysis} onClose={() => setPreview(null)} />}
    </Stack>
  );
}
